import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';

import { useQueryClient } from '@tanstack/react-query';

import { RenderingErrorBoundaryChildProps } from 'components/RenderingErrorBoundary';
import AIButton from 'components/shared/ai/AIButton';
import Tooltip from 'components/shared/Tooltip';
import { Flags } from 'constants/flags';
import { useEpisode } from 'context/episode';
import { useSelectedLocationEpisode } from 'context/locationEpisode';
import { useProfile } from 'context/profile';
import { useTrackAIJob } from 'hooks/useTrackAIJob';
import { SummarizableReview } from 'lib/aiJobHelpers';
import { simpleDate } from 'lib/date';
import { AIJobType } from 'models/AIJob';
import { PERMISSIONS } from 'models/Profile';
import { ReviewType } from 'models/Review';
import Referral from 'models/reviews/Referral';
import { createReviewSummaryJob } from 'services/api/aiJobs';
import {
  authorizationQueryKeys,
  authorizationQueryParams,
  referralQueryKeys,
  referralQueryParams,
} from 'services/api/reviews';
import { AIJobNotificationType } from 'stores/aiJobNotificationStore';

import { getReturnUrlPath, getSummaryButtonTooltip } from './lib';
import SummaryContentView from './SummaryContentView';
import SummaryEmptyState from './SummaryEmptyState';
import SummaryLoadingState from './SummaryLoadingState';

function SummaryErrorState({
  onSummarizeFiles,
  disabled,
  tooltipText,
}: {
  onSummarizeFiles: () => void;
  disabled?: boolean;
  tooltipText?: string;
}) {
  return (
    <ErrorContainer>
      <span>Summary failed to process. Click below to try again.</span>
      <div style={{ width: 148 }}>
        <Tooltip text={tooltipText || ''} disabled={!disabled || !tooltipText}>
          <div>
            <AIButton onClick={onSummarizeFiles} fullWidth disabled={disabled}>
              Try Again
            </AIButton>
          </div>
        </Tooltip>
      </div>
    </ErrorContainer>
  );
}

type Props = RenderingErrorBoundaryChildProps & {
  review: SummarizableReview;
  pendingActions?: Array<'summarize' | 'compare'>;
  isCompared?: boolean;
  onCompareSummaries?: () => void;
  attachmentType?: string;
};

export default function SummaryTab({
  review,
  pendingActions = [],
  isErrored = false,
  isCompared = false,
  onCompareSummaries,
  resetErrorBoundary,
  attachmentType,
}: Props) {
  // Check if AI summary and comparison buttons should be disabled
  const { profile } = useProfile();
  const { episode } = useEpisode();
  const { locationEpisode } = useSelectedLocationEpisode();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { startTracking, isTracking } = useTrackAIJob();

  const canCreateSummary = profile?.has(PERMISSIONS.aiJobSummaryCreate, { episode, locationEpisode }) ?? false;
  const canCreateComparison = profile?.has(PERMISSIONS.aiJobComparisonCreate, { episode, locationEpisode }) ?? false;
  const isLocationEpisodeDischarged = locationEpisode?.discharged ?? false;

  const isAuthorization = review.type === ReviewType.AUTHORIZATION;
  const { summaryJob, summary } = review;
  const summaryJobInProgress = summaryJob && summaryJob.isProcessing && !summary;
  const filesCount = review?.attachmentCount ?? review?.attachments?.length ?? 0;

  const allAttachmentsFailedProcessing = review.attachments.every(
    (attachment) => attachment.extractDataAIJob?.isErrored
  );

  const shouldDisableSummaryButton = allAttachmentsFailedProcessing || !canCreateSummary || isLocationEpisodeDischarged;
  const shouldDisableComparisonButton =
    (!isCompared && (!canCreateComparison || isLocationEpisodeDischarged)) ||
    (isAuthorization && !review.previousAuthorizationReview?.summary);

  const summaryButtonTooltip = getSummaryButtonTooltip({
    reviewType: review.type,
    attachments: review.attachments,
    allAttachmentsFailedProcessing,
  });

  const [isCreatingJob, setIsCreatingJob] = useState(false);

  const shouldShowLoadingState = isCreatingJob || summaryJobInProgress;

  const [showLoadingState, setShowLoadingState] = useState(shouldShowLoadingState);

  const queryKey = (isAuthorization ? authorizationQueryKeys : referralQueryKeys).show({ id: review.id });
  const patientName = (() => {
    if (episode?.patient) return episode.patient.name;
    if (review.type === ReviewType.REFERRAL) return (review as Referral).episode.patient.name;
    return 'patient';
  })();

  const handleSummarizeFiles = useCallback(async () => {
    resetErrorBoundary?.();
    setIsCreatingJob(true);
    try {
      const reviewInclude = (isAuthorization ? authorizationQueryParams : referralQueryParams)
        .show(review.id)
        .include.split(',')
        .map((path) => `review.${path}`)
        .join(',');

      const updatedReview = await createReviewSummaryJob(review.id, reviewInclude);
      queryClient.setQueryData(queryKey, updatedReview);

      if (updatedReview.summaryJob) {
        startTracking({
          jobs: [{ id: updatedReview.summaryJob.id, type: AIJobType.SUMMARY }],
          jobType: isAuthorization
            ? AIJobNotificationType.AUTHORIZATION_SUMMARY
            : AIJobNotificationType.REFERRAL_SUMMARY,
          jobbableId: review.id,
          patientName: patientName,
          returnUrl: getReturnUrlPath(location.pathname),
        });
      }
    } catch {
      setIsCreatingJob(false);
    }
  }, [
    review.id,
    resetErrorBoundary,
    queryKey,
    startTracking,
    isAuthorization,
    patientName,
    location.pathname,
    queryClient,
  ]);

  // Check if we need to track on page load and on a job update
  useEffect(() => {
    const jobType = isAuthorization
      ? AIJobNotificationType.AUTHORIZATION_SUMMARY
      : AIJobNotificationType.REFERRAL_SUMMARY;
    if (
      summaryJobInProgress &&
      !isTracking({ jobbableId: review.id, jobType }) &&
      location.pathname.endsWith('/summary')
    ) {
      startTracking({
        jobs: [{ id: summaryJob.id, type: AIJobType.SUMMARY }],
        jobType,
        jobbableId: review.id,
        patientName: patientName,
        returnUrl: getReturnUrlPath(location.pathname),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [summaryJobInProgress, location.pathname]);

  // Handle pending actions from other tabs
  useEffect(() => {
    const summarizeIndex = pendingActions.findIndex((action) => action === 'summarize');
    if (summarizeIndex !== -1 && !summaryJob && !allAttachmentsFailedProcessing && !isCreatingJob) {
      pendingActions.splice(summarizeIndex, 1);
      handleSummarizeFiles();
    }
  }, [handleSummarizeFiles, pendingActions, summaryJob, filesCount, isCreatingJob, allAttachmentsFailedProcessing]);

  useEffect(() => {
    if (shouldShowLoadingState) {
      setShowLoadingState(true);
    }
  }, [shouldShowLoadingState]);

  useEffect(() => {
    if (isCreatingJob && summaryJobInProgress) setIsCreatingJob(false);
  }, [summaryJobInProgress, isCreatingJob]);

  const handleLoadingFinished = useCallback(() => {
    setShowLoadingState(false);
  }, []);

  // Error state
  if (isErrored || summaryJob?.isErrored) {
    return (
      <SummaryErrorState
        onSummarizeFiles={handleSummarizeFiles}
        disabled={shouldDisableSummaryButton}
        tooltipText={summaryButtonTooltip}
      />
    );
  }

  if (showLoadingState) {
    return <SummaryLoadingState review={review} onLoadingFinished={handleLoadingFinished} />;
  }

  if (!summary) {
    return (
      <SummaryEmptyState
        filesCount={filesCount}
        summaryType={isAuthorization ? ReviewType.AUTHORIZATION : ReviewType.REFERRAL}
        onSummarizeFiles={handleSummarizeFiles}
        disableSummaryButton={shouldDisableSummaryButton}
        tooltipText={summaryButtonTooltip}
      />
    );
  }

  const comparisonAction =
    profile.hasFlag(Flags.ComparisonForAiSummary) && isAuthorization && !!review.previousAuthorizationReview ? (
      <AIButton data-cy='CompareSummaries' disabled={shouldDisableComparisonButton} onClick={onCompareSummaries}>
        {isCompared ? 'View Comparison' : 'Compare'} to{' '}
        {simpleDate(review.previousAuthorizationReview.approvedThroughDate)}
      </AIButton>
    ) : undefined;

  return (
    <SummaryContentView
      summaryJob={summaryJob}
      summary={summary}
      attachments={review.attachments}
      attachmentType={attachmentType}
      queryKey={queryKey}
      actions={comparisonAction}
    />
  );
}

const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 24px;
  height: 100%;

  font-weight: 400;
  font-size: 16px;
  line-height: 16px;
  letter-spacing: 2%;
  text-align: center;
`;
